import { isValidBusinessDay } from "@redotech/redo-common-model/date/business-day";
import { isValidDeliveryDate } from "@redotech/redo-conversion-core-common/conversion/delivery-date/valid-delivery-date";
import {
  Carriers,
  UpsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  carrierToLabel,
  servicesMatch,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { upsGroundHasSaturdayDelivery } from "@redotech/redo-fulfillment-carrier-common/ups-ground-saturday-delivery";
import {
  type CheapestWithinDaysSortingCriteria,
  RateSelectionCriteria,
  SortingCriteria,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { SelectedShippingRateSetBy } from "@redotech/redo-fulfillment-group-common/selected-shipping-rate-set-by";
import {
  carrierAccountIdMatchesRate,
  isAliasedAmazonRate,
} from "@redotech/redo-fulfillment-rate-common/amazon-shipping-alias";
import { OutboundRate } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { TemporalDayOfWeek } from "@redotech/util/temporal";
import { assertNever } from "@redotech/util/type";

export interface RateSelectionResult {
  rate: OutboundRate;
  setBy: SelectedShippingRateSetBy;
}

export interface RateSelectionGroupDependencies {
  selectedShippingRateSetBy: SelectedShippingRateSetBy | undefined | null;
  rateSelectionCriteria: RateSelectionCriteria | undefined | null;
  destinationPostalCode?: string | null;
}

/**
 * The number of days the package will spend in transit. Prefer `transit_days`
 * (the carrier's SLA expressed as a count, stable across quote days) over
 * `delivery_days`, which some providers report relative to "now" so it drifts
 * — e.g. EasyPost FedEx 2Day reports 4 when quoted Thursday, 2 when quoted
 * Monday.
 */
export function getRateTransitDays(rate: OutboundRate): number | undefined {
  if (rate.transit_days != null) {
    return rate.transit_days;
  }
  if (rate.delivery_days != null) {
    return rate.delivery_days;
  }
  return undefined;
}

/**
 * Calendar date the rate is expected to deliver on, anchored to a given ship
 * date. Walks forward from `shipDate` counting carrier-valid delivery days
 * (per the model's per-carrier weekend rules, with the EasyPost
 * `saturday_delivery` surcharge as an override) until the rate's transit
 * budget is exhausted.
 *
 * Used by automation rate selection so two rates with the same transit_days
 * count are still ordered by their actual arrival day. UPS Ground transit=3
 * shipped Wed to a Saturday-eligible ZIP arrives Sat; FedEx 2Day without the
 * Saturday option shipped Wed arrives Fri. The transit count alone misranks
 * them.
 */
export function getRateExpectedDeliveryDate({
  rate,
  shipDate,
  destinationPostalCode,
}: {
  rate: OutboundRate;
  shipDate: Temporal.PlainDate;
  destinationPostalCode?: string | null;
}): Temporal.PlainDate | undefined {
  const transitDays = getRateTransitDays(rate);
  if (transitDays == null || transitDays <= 0 || transitDays > 100) {
    return undefined;
  }

  let date = shipDate;
  let remaining = transitDays;
  // Hard upper bound on the walk in case a carrier has no valid delivery day
  // (would otherwise loop forever).
  let safety = transitDays + 30;
  while (remaining > 0 && safety > 0) {
    date = date.add({ days: 1 });
    if (isDeliveryDayForRate({ date, rate, destinationPostalCode })) {
      remaining--;
    }
    safety--;
  }

  return remaining === 0 ? date : undefined;
}

function isDeliveryDayForRate({
  date,
  rate,
  destinationPostalCode,
}: {
  date: Temporal.PlainDate;
  rate: OutboundRate;
  destinationPostalCode?: string | null;
}): boolean {
  const isUpsGround =
    rate.carrier === Carriers.UPS && rate.service === UpsServices.Ground;
  const isSaturday = date.dayOfWeek === TemporalDayOfWeek.SATURDAY;
  if (isUpsGround && isSaturday) {
    return upsGroundHasSaturdayDelivery(destinationPostalCode);
  }

  const hasSaturdayDeliveryOverride = rate.saturday_delivery && !isUpsGround;
  if (hasSaturdayDeliveryOverride && isSaturday) {
    return true;
  }
  return isValidDeliveryDate({
    date: date.toZonedDateTime("UTC"),
    carrier: rate.carrier,
    service: rate.service,
  });
}

function isDayCountedForDeliveryWindow({
  date,
  rate,
  sortingCriteria,
  destinationPostalCode,
}: {
  date: Temporal.PlainDate;
  rate: OutboundRate;
  sortingCriteria: CheapestWithinDaysSortingCriteria;
  destinationPostalCode?: string | null;
}): boolean {
  switch (sortingCriteria) {
    case SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS:
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    case SortingCriteria.CHEAPEST_WITHIN_BUSINESS_DAYS:
      return isValidBusinessDay({
        date: date.toZonedDateTime("UTC"),
        businessEntity: undefined,
      });
    case SortingCriteria.CHEAPEST_WITHIN_DELIVERABLE_DAYS:
      return isDeliveryDayForRate({ date, rate, destinationPostalCode });
    default:
      return assertNever(sortingCriteria);
  }
}

/**
 * How many days from `from` until the rate actually delivers, assuming it
 * ships on `shipDate` (typically `from` itself when running automations at
 * "now"). `sortingCriteria` controls whether the delivery window counts the
 * existing delivery-day behavior (every day except Sunday), business days, or
 * only days when that carrier and service can deliver.
 *
 * The pipeline is:
 *
 *   transit_days → expected delivery date (carrier rules) → days until then
 *
 * This is the single number used for "fastest" comparison and for the
 * "cheapest within N days" threshold, so both criteria reflect when the
 * package will actually arrive — not just the carrier's transit count.
 *
 * Falls back to the raw transit count when the delivery date can't be
 * computed (unknown carrier rules), so the rate is still rankable instead of
 * silently dropped.
 */
export function getRateDaysUntilDelivery({
  rate,
  shipDate,
  from,
  sortingCriteria = SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS,
  destinationPostalCode,
}: {
  rate: OutboundRate;
  shipDate: Temporal.PlainDate;
  from: Temporal.PlainDate;
  sortingCriteria?: CheapestWithinDaysSortingCriteria;
  destinationPostalCode?: string | null;
}): number | undefined {
  const deliveryDate = getRateExpectedDeliveryDate({
    rate,
    shipDate,
    destinationPostalCode,
  });
  if (!deliveryDate) {
    return getRateTransitDays(rate);
  }
  let count = 0;
  let cursor = from;
  while (Temporal.PlainDate.compare(cursor, deliveryDate) < 0) {
    cursor = cursor.add({ days: 1 });
    if (
      isDayCountedForDeliveryWindow({
        date: cursor,
        rate,
        sortingCriteria,
        destinationPostalCode,
      })
    ) {
      count++;
    }
  }
  return count;
}

/**
 * Landed cost (duties + taxes + fees) attached to a rate, in minor units.
 * `fees` already folds in Redo's per-order fee and excludes the provider's
 * per-order fee (see `attachLandedCostToRates`), so this sum is the merchant-
 * charged total — the same figure checkout exposes as `landedCostTotal`.
 *
 * Returns 0 when no landed cost is attached (domestic shipments, or LCG
 * disabled for the team), and also when the landed cost is quoted in a
 * different currency than the rate. Outbound rate amounts are always USD,
 * but `attachLandedCostToRates` quotes landed cost in the order's currency —
 * so on a non-USD order the two cannot be summed. A mismatch falls back to
 * the bare USD rate (the pre-landed-cost behavior) rather than adding
 * mismatched currencies.
 */
export function getRateLandedCostMinor(rate: OutboundRate): number {
  const landedCost = rate.landedCost;
  if (!landedCost || landedCost.currency !== rate.currency) {
    return 0;
  }
  return landedCost.duties + landedCost.taxes + landedCost.fees;
}

/**
 * Total cost of a rate in minor units: the shipping rate plus any attached
 * landed cost. Mirrors checkout's `rateTotalCost` so OMS ranks rates the same
 * way checkout does — cheapest *including* Landed Cost Guarantee.
 */
export function getRateTotalCostMinor(rate: OutboundRate): number {
  return Math.round(Number(rate.rate) * 100) + getRateLandedCostMinor(rate);
}

export function sortOutboundRates(rates: OutboundRate[]): OutboundRate[] {
  const sortedRates = rates.toSorted((a, b) => {
    const diff = getRateTotalCostMinor(a) - getRateTotalCostMinor(b);

    // If the total costs are equal, sort by the spread + rev share margin
    if (diff === 0 && a.provider_cost && b.provider_cost) {
      const aSpread = parseFloat(a.rate) - parseFloat(a.provider_cost);
      const bSpread = parseFloat(b.rate) - parseFloat(b.provider_cost);

      const aTotalProfit = aSpread + parseFloat(a.rev_share_margin || "0");
      const bTotalProfit = bSpread + parseFloat(b.rev_share_margin || "0");

      return bTotalProfit - aTotalProfit;
    }

    return diff;
  });

  return sortedRates;
}

export function ratesEqual(a: OutboundRate, b: OutboundRate): boolean {
  const carrierServiceProviderEqual =
    a.service === b.service &&
    a.carrier === b.carrier &&
    a.provider === b.provider;
  // Backwards compatibility for IB shipping provider updated carrier account id
  const carrierAccountIdsEqual =
    a.carrier_account_id === b.carrier_account_id ||
    (a.carrier_account_id === "" && b.carrier_account_id === "IB") ||
    (a.carrier_account_id === "IB" && b.carrier_account_id === "");
  return (
    (carrierServiceProviderEqual && carrierAccountIdsEqual) ||
    isAliasedAmazonRate(a, b)
  );
}

const rateMatchesCriteria = (
  rate: OutboundRate,
  rateOption: NonNullable<RateSelectionCriteria["ratesToChooseFrom"]>[number],
): boolean => {
  const carrierAndServiceMatch =
    carrierToLabel[rateOption.carrier] === carrierToLabel[rate.carrier] &&
    servicesMatch(rateOption.service, rate.service);

  if (!carrierAndServiceMatch) {
    return false;
  }

  return rateOption.carrier_account_id
    ? carrierAccountIdMatchesRate(rateOption.carrier_account_id, rate)
    : true;
};

export function handlePriorityRates(
  bestRate: OutboundRate,
  bestPriorityRate: OutboundRate | undefined,
  maxPriorityPriceDifference: number,
): OutboundRate | undefined {
  if (!bestPriorityRate) {
    return bestRate;
  }

  const priceDifference = Number(bestPriorityRate.rate) - Number(bestRate.rate);

  if (
    priceDifference <= maxPriorityPriceDifference &&
    !ratesEqual(bestRate, bestPriorityRate)
  ) {
    return bestPriorityRate;
  }

  return bestRate;
}

export function getBestRateForGivenCriteria({
  availableRates,
  rateSelectionCriteria,
  destinationPostalCode,
}: {
  availableRates: OutboundRate[] | null | undefined;
  rateSelectionCriteria?: RateSelectionCriteria;
  destinationPostalCode?: string | null;
}): OutboundRate | undefined {
  if (!availableRates || !rateSelectionCriteria) {
    return undefined;
  }

  const filteredRates = availableRates.filter((rate) => {
    return rateSelectionCriteria.ratesToChooseFrom.some((rateOption) =>
      rateMatchesCriteria(rate, rateOption),
    );
  });

  const priorityRates = availableRates?.filter((rate) => {
    return rateSelectionCriteria.ratesToPrioritize?.some((rateOption) =>
      rateMatchesCriteria(rate, rateOption),
    );
  });

  if (filteredRates[0] === undefined) {
    return undefined;
  }

  switch (rateSelectionCriteria.sortingCriteria) {
    case SortingCriteria.CHEAPEST: {
      const cheapestRate = sortOutboundRates(filteredRates)[0];
      const cheapestPriorityRate = sortOutboundRates(priorityRates)[0];

      return handlePriorityRates(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        cheapestRate,
        cheapestPriorityRate,
        rateSelectionCriteria.maxPriorityPriceDifference ?? 0,
      );
    }
    case SortingCriteria.FASTEST: {
      const today = Temporal.Now.plainDateISO();
      const daysUntil = (rate: OutboundRate): number =>
        getRateDaysUntilDelivery({
          rate,
          shipDate: today,
          from: today,
          destinationPostalCode,
        }) ?? Infinity;

      const minDays = Math.min(...filteredRates.map(daysUntil));
      const fastestRates = filteredRates.filter(
        (rate) => daysUntil(rate) === minDays,
      );
      const fastestPriorityRates = priorityRates?.filter(
        (rate) => daysUntil(rate) === minDays,
      );

      const fastestCheapestRate = sortOutboundRates(fastestRates)[0];

      const fastestCheapestPriorityRate =
        sortOutboundRates(fastestPriorityRates)[0];

      return handlePriorityRates(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fastestCheapestRate,
        fastestCheapestPriorityRate,
        rateSelectionCriteria.maxPriorityPriceDifference ?? 0,
      );
    }
    case SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS:
    case SortingCriteria.CHEAPEST_WITHIN_BUSINESS_DAYS:
    case SortingCriteria.CHEAPEST_WITHIN_DELIVERABLE_DAYS: {
      if (!rateSelectionCriteria.targetDeliveryDays) {
        return undefined;
      }

      const today = Temporal.Now.plainDateISO();
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const target = rateSelectionCriteria.targetDeliveryDays!;
      const sortingCriteria = rateSelectionCriteria.sortingCriteria;

      const fitsWithinTarget = (rate: OutboundRate): boolean => {
        const daysUntil = getRateDaysUntilDelivery({
          rate,
          shipDate: today,
          from: today,
          sortingCriteria,
          destinationPostalCode,
        });
        return daysUntil != null && daysUntil <= target;
      };

      const ratesWithinDeliveryDays = filteredRates.filter(fitsWithinTarget);

      if (ratesWithinDeliveryDays.length === 0) {
        return undefined;
      }

      const priorityRatesWithinDeliveryDays =
        priorityRates?.filter(fitsWithinTarget);

      const cheapestRateWithinDays = sortOutboundRates(
        ratesWithinDeliveryDays,
      )[0];

      const cheapestPriorityRateWithinDays = sortOutboundRates(
        priorityRatesWithinDeliveryDays,
      )[0];

      return handlePriorityRates(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        cheapestRateWithinDays,
        cheapestPriorityRateWithinDays,
        rateSelectionCriteria.maxPriorityPriceDifference ?? 0,
      );
    }
    default: {
      assertNever(rateSelectionCriteria.sortingCriteria);
    }
  }
}

/**
 * OMS fulfillment picker: ranks rates already returned by getV2OutboundRatesForOms.
 * Intentional: filterFedexOneRateRates (FedEx One Rate + UPS Simple Rate) is
 * Chop-only (applyFedexOneRateCheckoutFilter, getCheckoutRatesProviderInfo).
 */
export function chooseSelectedRateForGroup(
  groupDependencies: RateSelectionGroupDependencies,
  rates: OutboundRate[],
): RateSelectionResult | undefined {
  let rateSetBy: SelectedShippingRateSetBy | undefined | null;
  let selectedRate: OutboundRate | undefined | null;

  // RateSelectionCriteria will be defined for learning, checkout personalization, user choice, or automations
  if (groupDependencies.rateSelectionCriteria) {
    selectedRate = getBestRateForGivenCriteria({
      availableRates: rates,
      rateSelectionCriteria: groupDependencies.rateSelectionCriteria,
      destinationPostalCode: groupDependencies.destinationPostalCode,
    });

    // If selectedShippingRateSetBy is already defined, it was probably user, learning, or checkout personalization (or a previous automation)
    if (selectedRate) {
      rateSetBy =
        groupDependencies.selectedShippingRateSetBy ??
        SelectedShippingRateSetBy.AUTOMATIONS;

      return { rate: selectedRate, setBy: rateSetBy };
    }
  }

  // If there is no rate matching the rateSelectionCriteria, default to the cheapest rates
  const cheapestRate = sortOutboundRates(rates)[0];

  if (cheapestRate) {
    return {
      rate: cheapestRate,
      setBy: SelectedShippingRateSetBy.DEFAULT_CHEAPEST,
    };
  }

  // This should only happen if we have no rates
  return undefined;
}
